import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import img1 from "../../img/10276.jpg";
import img2 from "../../img/16522.jpg";
import img3 from "../../img/2150010142.jpg";

export default function CSS_MediaQueries() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">📱 Advanced CSS: Media Queries</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        Media queries allow CSS to respond to different screen sizes,
        resolutions, and device characteristics. Using media queries, you can
        build **responsive layouts** that adapt seamlessly to desktops, tablets,
        and mobile devices.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg,#fef9c3,#facc15)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#78350f" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>@media</code> to apply styles conditionally.
          </li>
          <li>Combine media queries with CSS variables for adaptive design.</li>
          <li>
            Breakpoints should be based on content, not just device sizes.
          </li>
          <li>Test responsiveness on multiple devices and resolutions.</li>
          <li>Can target width, height, orientation, resolution, and more.</li>
        </ul>
      </div>

      <div className="tutorial-section">
        <h2 className="section-title">📝 Media Query Examples</h2>

        {/* 1. Basic Media Query */}
        <h3>1. Basic Media Query</h3>
        <CodeCopyBlock
          code={`/* Default style */
.box {
  background: #3b82f6;
  color: white;
  padding: 20px;
}

/* For screens larger than 768px */
@media (min-width: 768px) {
  .box {
    background: #9333ea;
    padding: 40px;
  }
}`}
        />
        <div
          className="box"
          style={{
            background: "#3b82f6",
            color: "white",
            padding: "20px",
            borderRadius: "8px",
            textAlign: "center",
            marginBottom: "1rem",
          }}
        >
          Basic Media Query Box
        </div>

        {/* 2. Responsive Typography */}
        <h3>2. Responsive Typography</h3>
        <CodeCopyBlock
          code={`:root {
  --font-size: 16px;
}
@media (min-width: 768px) {
  :root { --font-size: 18px; }
}
@media (min-width: 1200px) {
  :root { --font-size: 20px; }
}
p {
  font-size: var(--font-size);
}`}
        />
        <p style={{ fontSize: "var(--font-size,16px)", marginBottom: "1rem" }}>
          This paragraph adjusts font size based on screen width using media
          queries and CSS variables.
        </p>

        {/* 3. Responsive Grid */}
        <h3>3. Responsive Grid Layout</h3>
        <CodeCopyBlock
          code={`.grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}
@media (min-width: 600px) {
  .grid { grid-template-columns: repeat(2, 1fr); }
}
@media (min-width: 1024px) {
  .grid { grid-template-columns: repeat(3, 1fr); }
}`}
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: "10px",
            marginBottom: "1rem",
          }}
          className="grid"
        >
          <div
            style={{
              background: "#fbbf24",
              padding: "20px",
              borderRadius: "8px",
            }}
          >
            1
          </div>
          <div
            style={{
              background: "#f97316",
              padding: "20px",
              borderRadius: "8px",
            }}
          >
            2
          </div>
          <div
            style={{
              background: "#3b82f6",
              padding: "20px",
              borderRadius: "8px",
            }}
          >
            3
          </div>
        </div>

        {/* 4. Orientation Query */}
        <h3>4. Orientation Query</h3>
        <CodeCopyBlock
          code={`@media (orientation: landscape) {
  .box {
    background: #16a34a;
  }
}`}
        />
        <div
          style={{
            background: "#3b82f6",
            color: "white",
            padding: "20px",
            borderRadius: "8px",
            marginBottom: "1rem",
          }}
        >
          Landscape Orientation Box
        </div>

        {/* 5. Combining Variables + Media Queries */}
        <h3>5. Variables + Media Queries</h3>
        <CodeCopyBlock
          code={`:root {
  --card-padding: 10px;
}
@media(min-width:768px){
  :root { --card-padding: 20px; }
}
.card {
  padding: var(--card-padding);
  background: #fde68a;
  border-radius: 12px;
}`}
        />
        <div
          style={{
            padding: "var(--card-padding,10px)",
            background: "#fde68a",
            borderRadius: "12px",
            textAlign: "center",
            marginBottom: "2rem",
          }}
        >
          Card with Responsive Padding
        </div>
      </div>

      {/* SECTION 1: Navbar */}
      <div className="tutorial-section">
        <h2 className="section-title">🧭 Responsive Navbar</h2>

        <CodeCopyBlock
          code={`.navbar {
  display: flex;
  justify-content: space-between;
  background: #3b82f6;
  padding: 10px 20px;
  border-radius: 8px;
  color: white;
}
.nav-links {
  display: flex;
  gap: 15px;
}
@media (max-width: 600px) {
  .nav-links {
    flex-direction: column;
    gap: 10px;
  }
}`}
        />

        <div
          className="navbar"
          style={{
            display: "flex",
            justifyContent: "space-between",
            background: "#3b82f6",
            padding: "10px 20px",
            borderRadius: "8px",
            color: "white",
            marginBottom: "1rem",
          }}
        >
          <span>Logo</span>
          <div
            className="nav-links"
            style={{
              display: "flex",
              gap: "15px",
              flexWrap: "wrap",
            }}
          >
            <a href="#" style={{ color: "white" }}>
              Home
            </a>
            <a href="#" style={{ color: "white" }}>
              About
            </a>
            <a href="#" style={{ color: "white" }}>
              Services
            </a>
            <a href="#" style={{ color: "white" }}>
              Contact
            </a>
          </div>
        </div>

        {/* SECTION 2: Image Card */}
        <h2 className="section-title">🖼️ Responsive Image Card</h2>

        <CodeCopyBlock
          code={`.card {
  max-width: 300px;
  border: 2px solid #9333ea;
  border-radius: 12px;
  overflow: hidden;
}
.card img {
  width: 100%;
  display: block;
}
.card-content {
  padding: 15px;
}
@media (max-width: 500px) {
  .card {
    max-width: 100%;
  }
}`}
        />

        <div
          className="card"
          style={{
            maxWidth: "300px",
            border: "2px solid #9333ea",
            borderRadius: "12px",
            overflow: "hidden",
            marginBottom: "1rem",
          }}
        >
          <img
            src={img1}
            alt="Card"
            style={{ width: "100%", display: "block" }}
          />
          <div className="card-content" style={{ padding: "15px" }}>
            <h3>Card Title</h3>
            <p>Responsive image card with media query adjustments.</p>
          </div>
        </div>

        {/* SECTION 3: Image Gallery */}
        <h2 className="section-title">🖼️ Responsive Image Gallery</h2>

        <CodeCopyBlock
          code={`.gallery {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
@media (max-width: 800px) {
  .gallery {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 500px) {
  .gallery {
    grid-template-columns: 1fr;
  }
}`}
        />

        <div
          className="gallery"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "10px",
            marginBottom: "1rem",
          }}
        >
          <img src={img1} alt="Gallery 1" style={{ width: "100%" }} />
          <img src={img2} alt="Gallery 2" style={{ width: "100%" }} />
          <img src={img3} alt="Gallery 3" style={{ width: "100%" }} />
          <img src={img1} alt="Gallery 4" style={{ width: "100%" }} />
          <img src={img2} alt="Gallery 5" style={{ width: "100%" }} />
          <img src={img3} alt="Gallery 6" style={{ width: "100%" }} />
        </div>

        {/* SECTION 4: Responsive Form */}
        <h2 className="section-title">✏️ Responsive Form</h2>

        <CodeCopyBlock
          code={`.form-container {
  max-width: 400px;
  padding: 20px;
  border: 2px solid #f97316;
  border-radius: 12px;
}
.form-container input,
.form-container button,
.form-container textarea {
  width: 100%;
  margin-bottom: 10px;
  padding: 10px;
}
@media (max-width: 500px) {
  .form-container {
    padding: 15px;
  }
}`}
        />

        <form
          className="form-container"
          style={{
            maxWidth: "400px",
            padding: "20px",
            border: "2px solid #f97316",
            borderRadius: "12px",
            marginBottom: "2rem",
          }}
        >
          <input
            type="text"
            placeholder="Name"
            style={{ width: "100%", marginBottom: "10px", padding: "10px" }}
          />
          <input
            type="email"
            placeholder="Email"
            style={{ width: "100%", marginBottom: "10px", padding: "10px" }}
          />
          <textarea
            placeholder="Message"
            style={{ width: "100%", marginBottom: "10px", padding: "10px" }}
          />
          <button
            style={{
              width: "100%",
              padding: "10px",
              background: "#f97316",
              color: "white",
              border: "none",
              borderRadius: "6px",
            }}
          >
            Submit
          </button>
        </form>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg,#fef9c3,#facc15)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#78350f" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Combine media queries with CSS variables for full responsive design.
          </li>
          <li>
            Use custom breakpoints based on content width, not just device size.
          </li>
          <li>
            Experiment with orientation, resolution, and aspect-ratio queries.
          </li>
          <li>
            Create fully responsive grids, cards, buttons, and typography.
          </li>
        </ul>
      </div>
    </div>
  );
}
